import { useState } from "react";
import { Loader2, Mail } from "lucide-react";

// "I registered but I've lost the email." Reached from the footer.
//
// This page deliberately shows NOTHING about a booking. It asks for an email
// and sends that address a link — no more. Displaying someone's registration
// to anybody who typed their address would let a stranger cancel their seats,
// and an email address is not a secret. Reading the inbox is the proof.
export default function FindBooking() {
  const base = import.meta.env.VITE_APP_API_BASE_URL || "";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError("");
    setSent("");
    try {
      const res = await fetch(`${base}/send-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) {
        setError("Something went wrong. Please try again in a moment.");
        return;
      }
      setSent(body.message);
    } catch {
      setError("Could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#fdf8f1] py-12 text-[#2a1a12]">
      <div className="mx-auto w-full max-w-lg px-5">
        <div className="mb-7 flex items-center gap-3">
          <img
            src="/assets/Avdheshanandg mission logo.png"
            alt="AvdheshanandG Mission"
            className="h-9 w-auto"
          />
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#9d1f1c]">
            AvdheshanandG Mission
          </span>
        </div>

        <h1 className="text-2xl font-semibold text-[#5a0000]">
          Change or cancel your registration
        </h1>
        <p className="mt-2 leading-7 text-[#6b5545]">
          Enter the email address you registered with. We will send you a link to your booking,
          where you can see everyone attending, remove one guest, or cancel altogether.
        </p>

        {sent ? (
          <div className="mt-7 rounded-xl border border-green-200 bg-green-50 p-5">
            <p className="flex items-start gap-2 text-sm leading-6 text-green-900">
              <Mail size={18} className="mt-0.5 shrink-0" />
              {sent}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 rounded-xl border border-[#e6d6bd] bg-white p-5 shadow-sm">
            <label htmlFor="find-email" className="block text-sm font-medium text-[#4b2418]">
              Email address
            </label>
            <input
              id="find-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              disabled={busy}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              /* text-base, not smaller: anything under 16px makes iOS Safari
                 zoom the whole page the moment the field is focused. */
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#5a0000]/30 disabled:bg-gray-100"
            />
            {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#5a0000] px-5 py-3 text-sm font-medium text-white hover:bg-[#7a0000] disabled:opacity-60"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {busy ? "Sending…" : "Email me my booking link"}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-xs leading-6 text-[#6b5545]">
          Still stuck? Write to{" "}
          <a
            href="mailto:office@avdheshanandgmission.org"
            className="text-[#9d1f1c] underline-offset-2 hover:underline"
          >
            office@avdheshanandgmission.org
          </a>{" "}
          and we will help.
        </p>
      </div>
    </main>
  );
}
